/**
 * useScope — the full CD-8 URL grammar (S3); absorbed S2's useScorecardScope.
 * Grammar (OrderBook's ?drill= pattern adapted): site=Group|Kirkstall|Oracle ·
 * cadence=monthly|weekly · from/to=§5 period labels (URL wins on arrival; the
 * mount pins them when absent, a DateRangeSelector move re-writes them) ·
 * drill_metric=<registry id> · drill_period=YYYY-MM (§6: month-grained) ·
 * drill=<URL-encoded JSON [{level,id}...]> — the §5 path, carried verbatim.
 * Round-trip safe: any malformed drill param parses to a clean no-drill state,
 * never a crash. Weekly cadence has no drill (§6) and no Group site (§4).
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, getISOWeek, getISOWeekYear, startOfYear } from 'date-fns'
import { useDateRange } from '../contexts/DateRangeContext'
import type { Cadence, PathSegment, Site } from '../types/metrics'

export const SITES: readonly Site[] = ['Group', 'Kirkstall', 'Oracle']
const WEEKLY_FALLBACK: Site = 'Kirkstall'
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/
const WEEK_RE = /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/
const DRILL_PARAMS = ['drill_metric', 'drill_period', 'drill'] as const

export interface DrillState {
  metricId: string
  period: string // YYYY-MM — drill is month-grained (§6)
  path: PathSegment[]
}

export interface Scope {
  site: Site
  cadence: Cadence
  periodFrom: string
  periodTo: string
  drill: DrillState | null
  setSite: (site: Site) => void
  setCadence: (cadence: Cadence) => void
  siteEnabled: (site: Site) => boolean
  openDrill: (metricId: string, period: string) => void // seeds site+month crumbs
  setDrillPath: (path: PathSegment[]) => void
  closeDrill: () => void
}

type SetParams = ReturnType<typeof useSearchParams>[1]

/** §5 period label for a date: '2026-06' monthly, '2026-W28' weekly (ISO week). */
export function periodLabel(day: Date, cadence: Cadence): string {
  if (cadence === 'monthly') return format(day, 'yyyy-MM')
  return `${getISOWeekYear(day)}-W${String(getISOWeek(day)).padStart(2, '0')}`
}

function readSite(raw: string | null, cadence: Cadence): Site {
  const requested = SITES.includes(raw as Site) ? (raw as Site) : 'Group'
  return cadence === 'weekly' && requested === 'Group' ? WEEKLY_FALLBACK : requested
}

function validPeriod(raw: string | null, cadence: Cadence): string | null {
  const shape = cadence === 'weekly' ? WEEK_RE : MONTH_RE
  return raw !== null && shape.test(raw) ? raw : null
}

/** §5 drill path param → typed segments; ANY malformed shape → null (no-drill). */
function parseDrillPath(raw: string | null): PathSegment[] | null {
  if (!raw) return []
  try {
    const loaded: unknown = JSON.parse(raw)
    if (!Array.isArray(loaded)) return null
    const segments: PathSegment[] = []
    for (const item of loaded) {
      const seg = item as { level?: unknown; id?: unknown }
      if (typeof seg?.level !== 'string' || !seg.level || typeof seg?.id !== 'string' || !seg.id)
        return null
      segments.push({ level: seg.level, id: seg.id })
    }
    return segments
  } catch {
    return null // parse error → clean no-drill state, never a crash
  }
}

function readDrill(params: URLSearchParams, cadence: Cadence): DrillState | null {
  if (cadence === 'weekly') return null // §6: drill is month-grained
  const metricId = params.get('drill_metric')
  const period = params.get('drill_period')
  if (!metricId || !period || !MONTH_RE.test(period)) return null
  const path = parseDrillPath(params.get('drill'))
  return path === null ? null : { metricId, period, path }
}

/** Pure updater: site chip (exported — see useLoadingSurface's goToSite). Weekly Group
 * stays disabled; a site CHANGE closes the drill (the old site's path would lie). */
export function withSite(previous: URLSearchParams, next: Site): URLSearchParams {
  const params = new URLSearchParams(previous)
  const cadence: Cadence = params.get('cadence') === 'weekly' ? 'weekly' : 'monthly'
  if (cadence === 'weekly' && next === 'Group') return params
  if (readSite(params.get('site'), cadence) !== next) DRILL_PARAMS.forEach((p) => params.delete(p))
  params.set('site', next)
  return params
}

/** Pure updater: cadence. Weekly falls Group back to Kirkstall (§4) and closes
 * the drill (§6: no weekly drill); the window params re-sync via the effect. */
function withCadence(previous: URLSearchParams, next: Cadence): URLSearchParams {
  const params = new URLSearchParams(previous)
  params.set('cadence', next)
  if (next === 'weekly') {
    DRILL_PARAMS.forEach((p) => params.delete(p))
    if (readSite(params.get('site'), 'monthly') === 'Group') params.set('site', WEEKLY_FALLBACK)
  }
  return params
}

function withDrill(previous: URLSearchParams, drill: DrillState | null): URLSearchParams {
  const params = new URLSearchParams(previous)
  DRILL_PARAMS.forEach((p) => params.delete(p))
  if (drill) {
    params.set('drill_metric', drill.metricId)
    params.set('drill_period', drill.period)
    if (drill.path.length) params.set('drill', JSON.stringify(drill.path))
  }
  return params
}

/** Serialize the context window into from/to when it CHANGES post-mount, and once on
 * mount when the URL lacks a valid window, so every copyable URL carries the rendered
 * window (CD-8); deep-linked from/to still win on arrival — they seed `last`. */
function useWindowSync(from: string, to: string, pin: boolean, set: SetParams): void {
  const last = useRef<string | null>(pin ? `${from}|${to}` : null)
  useEffect(() => {
    const pair = `${from}|${to}`
    if (last.current === pair) return
    last.current = pair
    set((previous) => {
      const params = new URLSearchParams(previous)
      params.set('from', from)
      params.set('to', to)
      return params
    }, { replace: true })
  }, [from, to, set])
}

/** The drill setters, split out to hold the CD-1 function budget in useScope. */
function useDrillSetters(site: Site, setSearchParams: SetParams) {
  const openDrill = useCallback(
    (metricId: string, period: string) => {
      if (!MONTH_RE.test(period)) return // §6: only month cells drill
      const month: PathSegment = { level: 'month', id: period }
      // Group is never a path site id (§5) — the union request carries no site crumb
      const path = site === 'Group' ? [month] : [{ level: 'site', id: site }, month]
      setSearchParams((previous) => withDrill(previous, { metricId, period, path }))
    },
    [site, setSearchParams]
  )
  const setDrillPath = useCallback(
    (path: PathSegment[]) =>
      setSearchParams((previous) => {
        const drill = readDrill(previous, 'monthly')
        return drill ? withDrill(previous, { ...drill, path }) : previous
      }),
    [setSearchParams]
  )
  const closeDrill = useCallback(() => setSearchParams((p) => withDrill(p, null)), [setSearchParams])
  return { openDrill, setDrillPath, closeDrill }
}

export function useScope(): Scope {
  const [searchParams, setSearchParams] = useSearchParams()
  const { dateRange } = useDateRange()

  const cadence: Cadence = searchParams.get('cadence') === 'weekly' ? 'weekly' : 'monthly'
  const site = readSite(searchParams.get('site'), cadence)

  const { contextFrom, contextTo } = useMemo(() => {
    const today = new Date()
    const start = (dateRange.startDate as Date | null) ?? startOfYear(today)
    const end = (dateRange.endDate as Date | null) ?? today
    return { contextFrom: periodLabel(start, cadence), contextTo: periodLabel(end, cadence) }
  }, [dateRange.startDate, dateRange.endDate, cadence])
  const urlFrom = validPeriod(searchParams.get('from'), cadence)
  const urlTo = validPeriod(searchParams.get('to'), cadence)
  useWindowSync(contextFrom, contextTo, urlFrom !== null && urlTo !== null, setSearchParams)
  const [periodFrom, periodTo] = [urlFrom ?? contextFrom, urlTo ?? contextTo]
  const drill = useMemo(() => readDrill(searchParams, cadence), [searchParams, cadence])

  const setSite = useCallback(
    (next: Site) => setSearchParams((previous) => withSite(previous, next)),
    [setSearchParams]
  )
  const setCadence = useCallback(
    (next: Cadence) => setSearchParams((previous) => withCadence(previous, next)),
    [setSearchParams]
  )
  const siteEnabled = useCallback(
    (candidate: Site) => !(cadence === 'weekly' && candidate === 'Group'),
    [cadence]
  )
  const drillSetters = useDrillSetters(site, setSearchParams)

  return { site, cadence, periodFrom, periodTo, drill, setSite, setCadence, siteEnabled, ...drillSetters }
}
